<template>
  <v-main class="student-library">
    <div class="student-library__inner">
      <header class="student-library__title">
        <div>
          <h1>Бібліотека</h1>
          <p>Підручники та навчальні матеріали для твоїх предметів</p>
        </div>
      </header>

      <section v-if="!isGuest" class="student-library__toolbar" aria-label="Фільтри бібліотеки">
        <label class="student-library__search">
          <v-icon color="#7a8592" size="21">mdi-magnify</v-icon>
          <input
            v-model.trim="search"
            type="search"
            placeholder="Знайти матеріал"
          />
        </label>
        <label class="student-library__filter">
          <span>Предмет</span>
          <select v-model="selectedSubject">
            <option value="">Усі предмети</option>
            <option v-for="subject in subjects" :key="subject" :value="subject">
              {{ subject }}
            </option>
          </select>
        </label>
      </section>

      <v-progress-linear
        v-if="loading && !isGuest"
        indeterminate
        class="student-library__progress theme-loader"
      />

      <section v-if="isGuest" class="student-library__state student-library__state--demo">
        <span><v-icon class="student-library__state-icon" size="34">mdi-book-lock-outline</v-icon></span>
        <small>Повна програма</small>
        <h2>Навчальні матеріали приховані в демо</h2>
        <p>
          Підручники, додаткові файли та матеріали до всіх предметів доступні
          після підключення повної навчальної програми.
        </p>
        <button type="button" @click="openDemoRequest">
          Залишити контакти
          <v-icon color="#fff" size="19">mdi-arrow-right</v-icon>
        </button>
      </section>

      <section v-else-if="error && !loading" class="student-library__state">
        <span
          ><v-icon color="#df3f35" size="34"
            >mdi-alert-circle-outline</v-icon
          ></span
        >
        <h2>Не вдалося завантажити бібліотеку</h2>
        <p>Перевір з’єднання та спробуй ще раз.</p>
        <button type="button" @click="loadBooks">Повторити</button>
      </section>

      <section
        v-else-if="!loading && filteredBooks.length"
        class="student-library__grid"
      >
        <article
          v-for="(book, index) in filteredBooks"
          :key="book.id || `${book.name}-${index}`"
          class="library-card"
        >
          <div class="library-card__top">
            <span class="library-card__cover" :style="coverStyle(book)">
              <v-icon :color="coverStyle(book).color" size="28">
                {{ book.subject_icon || 'mdi-book-open-page-variant-outline' }}
              </v-icon>
            </span>
            <div class="library-card__meta">
              <span v-if="book.subject_name" class="library-card__subject">{{
                book.subject_name
              }}</span>
              <span v-if="book.grade_name" class="library-card__grade"
                >{{ book.grade_name }} клас</span
              >
            </div>
          </div>

          <div class="library-card__body">
            <h2 :title="book.name">{{ book.name }}</h2>
            <p>{{ book.description || 'Опис матеріалу не додано' }}</p>
          </div>

          <footer class="library-card__footer">
            <a
              v-if="book.link"
              :href="bookLink(book.link)"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Відкрити матеріал</span>
              <v-icon size="19">mdi-open-in-new</v-icon>
            </a>
            <span v-else class="library-card__unavailable"
              >Матеріал поки недоступний</span
            >
          </footer>
        </article>
      </section>

      <section v-else-if="!loading" class="student-library__state">
        <span
          ><v-icon class="student-library__state-icon" size="34"
            >mdi-bookshelf</v-icon
          ></span
        >
        <h2>
          {{
            hasFilters ? 'Матеріалів не знайдено' : 'Бібліотека поки порожня'
          }}
        </h2>
        <p>
          {{
            hasFilters
              ? 'Спробуй змінити пошук або вибрати інший предмет.'
              : 'Навчальні матеріали з’являться тут пізніше.'
          }}
        </p>
        <button v-if="hasFilters" type="button" @click="resetFilters">
          Скинути фільтри
        </button>
      </section>
    </div>
  </v-main>
</template>

<script>
import popupEnum from '@/enums/popupEnum';

const CARD_COLORS = [
  { backgroundColor: '#eaf6f1', color: '#24795d' },
  { backgroundColor: '#eef4fc', color: '#2d68bd' },
  { backgroundColor: '#fff3e8', color: '#b3651f' },
  { backgroundColor: '#f4effa', color: '#7657a5' },
  { backgroundColor: '#fff7d9', color: '#9a7200' },
];

export default {
  name: 'LibraryPage',
  data: () => ({
    search: '',
    selectedSubject: '',
    loading: false,
    error: null,
  }),
  computed: {
    isGuest() {
      return this.$store.getters['UserStore/isGuest']();
    },
    books() {
      return this.$store.getters['LibraryStore/getBooks'];
    },
    subjects() {
      return [
        ...new Set(this.books.map((book) => book.subject_name).filter(Boolean)),
      ].sort((first, second) => first.localeCompare(second, 'uk'));
    },
    filteredBooks() {
      const query = this.search.toLocaleLowerCase('uk');
      return this.books.filter((book) => {
        const matchesSubject =
          !this.selectedSubject || book.subject_name === this.selectedSubject;
        const haystack = [
          book.name,
          book.description,
          book.subject_name,
          book.grade_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('uk');
        return matchesSubject && (!query || haystack.includes(query));
      });
    },
    hasFilters() {
      return Boolean(this.search || this.selectedSubject);
    },
  },
  mounted() {
    if (!this.isGuest) this.loadBooks();
  },
  methods: {
    async loadBooks() {
      this.loading = true;
      this.error = null;
      try {
        await this.$store.dispatch('LibraryStore/loadBooks');
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.search = '';
      this.selectedSubject = '';
    },
    openDemoRequest() {
      this.$store.dispatch('PopupStore/openPopup', {
        popupName: popupEnum.DEMO_REQUEST_POPUP,
        popupContext: {
          courseName: 'Бібліотека',
          lessonName: 'Підручники та навчальні матеріали',
        },
      });
    },
    coverStyle(book) {
      const value = String(book.subject_name || book.name || '');
      const hash = Array.from(value).reduce(
        (total, character) => total + character.charCodeAt(0),
        0
      );
      return CARD_COLORS[hash % CARD_COLORS.length];
    },
    bookLink(link) {
      try {
        const serverUrl = this.$store.getters['ConfigStore/getServerUrl'];
        const url = new URL(link, serverUrl);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
      } catch (error) {
        return '#';
      }
    },
  },
};
</script>

<style lang="less" scoped>
.student-library {
  background: var(--theme-app-background-accent);
  min-height: 100vh;
}
.student-library__inner {
  margin: 0 auto;
  max-width: 1480px;
  padding: 34px 28px 60px;
}
.student-library__title {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 22px;
}
.student-library__title h1 {
  color: #172033;
  font-size: 31px;
  margin: 0 0 7px;
}
.student-library__title p {
  color: #667085;
  margin: 0;
}
.student-library__toolbar {
  align-items: flex-end;
  background: var(--theme-banner-background);
  border: 1px solid var(--theme-banner-border);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.07);
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(260px, 1fr) minmax(210px, 300px);
  margin-bottom: 20px;
  padding: 15px 16px;
}
.student-library__search {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #dfe4e8;
  border-radius: 10px;
  display: flex;
  gap: 9px;
  min-height: 44px;
  padding: 0 13px;
}
.student-library__search:focus-within {
  border-color: var(--theme-accent-decorative);
  box-shadow: 0 0 0 3px var(--theme-surface-decorative);
}
.student-library__search input {
  background: transparent;
  border: 0;
  color: #172033;
  font: inherit;
  min-width: 0;
  outline: 0;
  width: 100%;
}
.student-library__filter span {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin: 0 0 5px 2px;
}
.student-library__filter select {
  appearance: auto;
  background: #f8fafb;
  border: 1px solid #dfe4e8;
  border-radius: 10px;
  color: #303b4c;
  font: inherit;
  min-height: 44px;
  padding: 0 12px;
  width: 100%;
}
.student-library__progress {
  border-radius: 999px;
  margin: 0 0 18px;
}
.student-library__grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
}
.library-card {
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 16px;
  box-shadow: 0 7px 24px rgba(31, 41, 55, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 310px;
  overflow: hidden;
  padding: 20px;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.library-card:hover {
  border-color: var(--theme-accent-decorative);
  box-shadow: 0 12px 30px rgba(31, 41, 55, 0.1);
  transform: translateY(-2px);
}
.library-card__top {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}
.library-card__cover {
  align-items: center;
  border-radius: 13px;
  display: flex;
  height: 54px;
  justify-content: center;
  width: 54px;
}
.library-card__meta {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 12px;
  min-width: 0;
}
.library-card__subject,
.library-card__grade {
  border-radius: 999px;
  display: block;
  font-size: 11px;
  font-weight: 800;
  max-width: 190px;
  overflow: hidden;
  padding: 5px 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.library-card__subject {
  background: var(--theme-surface-decorative);
  color: var(--theme-banner-text);
}
.library-card__grade {
  background: #f1f3f5;
  color: #667085;
}
.library-card__body {
  flex: 1 1 auto;
  padding: 19px 0 22px;
}
.library-card__body h2 {
  color: #172033;
  display: -webkit-box;
  font-size: 19px;
  line-height: 1.35;
  margin: 0 0 10px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.library-card__body p {
  color: #667085;
  display: -webkit-box;
  font-size: 14px;
  line-height: 1.55;
  margin: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}
.library-card__footer {
  border-top: 1px solid var(--theme-border-decorative);
  padding-top: 16px;
}
.library-card__footer a {
  align-items: center;
  background: var(--theme-header-background);
  border-radius: 10px;
  color: var(--theme-header-text);
  display: flex;
  font-size: 14px;
  font-weight: 800;
  justify-content: center;
  min-height: 44px;
  padding: 10px 14px;
  text-decoration: none;
}
.library-card__footer a span {
  margin-right: 8px;
}
.library-card__footer a:hover {
  filter: brightness(0.9);
}
.library-card__footer a::v-deep .v-icon {
  color: currentColor !important;
}
.library-card__unavailable {
  align-items: center;
  background: #f2f4f5;
  border-radius: 10px;
  color: #7b8591;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  justify-content: center;
  min-height: 44px;
  padding: 10px;
}
.student-library__state {
  align-items: center;
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  color: #667085;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 360px;
  padding: 36px;
  text-align: center;
}
.student-library__state > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 15px;
  display: flex;
  height: 60px;
  justify-content: center;
  width: 60px;
}
.student-library__state-icon {
  color: var(--theme-banner-text) !important;
}
.student-library__state h2 {
  color: #172033;
  margin: 17px 0 7px;
}
.student-library__state p {
  margin: 0 0 18px;
}
.student-library__state button {
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: var(--theme-header-text);
  cursor: pointer;
  font-weight: 800;
  min-height: 44px;
  padding: 10px 18px;
}
.student-library__state--demo {
  border-color: var(--theme-banner-border);
  min-height: 390px;
}
.student-library__state--demo > small {
  color: var(--theme-banner-text);
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .06em;
  margin-top: 15px;
  text-transform: uppercase;
}
.student-library__state--demo h2 {
  margin-top: 8px;
}
.student-library__state--demo p {
  line-height: 1.55;
  max-width: 620px;
}
.student-library__state--demo button {
  align-items: center;
  display: flex;
  gap: 8px;
}

@media (max-width: 760px) {
  .student-library__inner {
    padding: 24px 14px 40px;
  }
  .student-library__title h1 {
    font-size: 25px;
  }
  .student-library__toolbar {
    grid-template-columns: 1fr;
  }
  .student-library__grid {
    grid-template-columns: 1fr;
  }
}
</style>
